import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpRight, ShieldCheck, Layers, Truck } from 'lucide-react';
import styles from './HeroSection.module.css';

const WORDS = ['Sustainable', 'Premium', 'Reliable', 'Innovative'];

const PRODUCT_LINES = [
  { name: 'PE Coated Rolls', image: '/images/products/bobbins.jpeg' },
  { name: 'Food Wrap Paper', image: '/images/products/Food-Wrap-Paper.jpeg' },
  { name: 'Cup Sleeves', image: '/images/products/Paper-Cup-Sleeves.jpeg' },
  { name: 'Paper Plates', image: '/images/products/Paper-Plates.jpeg' },
];

const CAPABILITIES = [
  { icon: ShieldCheck, text: 'Food-grade QC' },
  { icon: Layers, text: 'Custom GSM & width' },
  { icon: Truck, text: 'Planned bulk dispatch' },
];

const HERO_IMAGE = '/images/hero-section.jpeg';

const tileMotion = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.25 + i * 0.07, duration: 0.5, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % WORDS.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className={styles.hero} aria-labelledby="home-hero-title">
      <div className={styles.media} aria-hidden="true">
        <img src={HERO_IMAGE} alt="" loading="eager" decoding="async" />
      </div>

      <div className={styles.scrim} aria-hidden="true" />
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.body}>
        <div className={styles.mainRow}>
          <motion.div
            className={styles.content}
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className={styles.badge}>
              <span className={styles.badgePulse} aria-hidden="true" />
              Greeneri Paper Tradelinks · Meerut
            </p>

            <h1 id="home-hero-title" className={styles.title}>
              <span className={styles.titleTop}>India&apos;s trusted partner for</span>
              <span className={styles.titleMain} aria-live="polite">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={WORDS[wordIndex]}
                    className={styles.titleWord}
                    initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
                    animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                    exit={{ opacity: 0, y: -18, filter: 'blur(6px)' }}
                    transition={{ duration: 0.45 }}
                  >
                    {WORDS[wordIndex]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <span className={styles.titleBottom}>paper manufacturing</span>
            </h1>

            <p className={styles.subtitle}>
              PE coated rolls, food wrap, cup stock and specialty grades — engineered
              for scale, delivered with consistency across India.
            </p>

            <div className={styles.actions}>
              <Link to="/products" className={styles.ctaPrimary}>
                View product range
                <ArrowUpRight size={18} strokeWidth={2.5} aria-hidden="true" />
              </Link>
              <Link to="/contact" className={styles.ctaOutline}>
                Request a quote
              </Link>
            </div>
          </motion.div>

          <motion.aside
            className={styles.showcase}
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.85, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
            aria-label="Core product lines"
          >
            <p className={styles.showcaseLabel}>What we manufacture</p>

            <div className={styles.productGrid}>
              {PRODUCT_LINES.map((product, index) => (
                <motion.div
                  key={product.name}
                  custom={index}
                  variants={tileMotion}
                  initial="hidden"
                  animate="visible"
                  className={styles.productTile}
                >
                  <img src={product.image} alt="" loading="lazy" decoding="async" />
                  <span className={styles.tileOverlay} aria-hidden="true" />
                  <span className={styles.tileName}>{product.name}</span>
                </motion.div>
              ))}
            </div>

            <ul className={styles.capabilities}>
              {CAPABILITIES.map(({ icon: Icon, text }) => (
                <li key={text}>
                  <Icon size={13} strokeWidth={2.4} aria-hidden="true" />
                  {text}
                </li>
              ))}
            </ul>
          </motion.aside>
        </div>
      </div>

      <div className={styles.wave} aria-hidden="true">
        <svg viewBox="0 0 1440 64" preserveAspectRatio="none">
          <path
            d="M0,32 C360,80 720,0 1080,32 C1260,48 1380,56 1440,48 L1440,64 L0,64 Z"
            fill="#f2f8f5"
          />
        </svg>
      </div>
    </section>
  );
}
